import type React from 'react';
import { useState } from 'react';
import { sydonAuthApi } from '../services/sydonApi';
import AuthShell from './AuthShell';

interface SignUpFormProps {
  onSwitchToLogin: () => void;
}

interface FormState {
  firstName: string;
  lastName: string;
  organizationName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const EMPTY_FORM: FormState = {
  firstName: '',
  lastName: '',
  organizationName: '',
  email: '',
  password: '',
  confirmPassword: '',
};

const LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  color: '#64748B',
  textTransform: 'uppercase',
  letterSpacing: '.05em',
  marginBottom: 6,
};

const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  padding: '11px 13px',
  border: '1px solid #E2E8F0',
  borderRadius: 10,
  fontSize: 13.5,
  color: '#0F172A',
  background: '#F8FAFC',
  boxSizing: 'border-box',
  outline: 'none',
};

const PRIMARY_BUTTON_STYLE: React.CSSProperties = {
  width: '100%',
  padding: '13px 20px',
  background: '#2563EB',
  color: '#fff',
  border: 'none',
  borderRadius: 12,
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
};

const SWITCH_LINK_STYLE: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#64748B',
  fontSize: 13,
  cursor: 'pointer',
};

const ERROR_BANNER_STYLE: React.CSSProperties = {
  background: '#FEF2F2',
  border: '1px solid #FEE2E2',
  borderRadius: 10,
  padding: '10px 14px',
  fontSize: 12.5,
  color: '#DC2626',
  marginBottom: 16,
};

function SwitchToLoginLink({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <button type="button" onClick={onClick} style={SWITCH_LINK_STYLE}>
      Already have an account? <span style={{ color: '#2563EB', fontWeight: 700 }}>Log in</span>
    </button>
  );
}

function CheckCircleIcon(): JSX.Element {
  return (
    <svg aria-hidden="true" width="40" height="40" viewBox="0 0 40 40" fill="none">
      <circle cx="20" cy="20" r="19" fill="#EFF6FF" stroke="#2563EB" strokeWidth="1.5" />
      <path
        d="M13 20.5l4.5 4.5L27.5 15"
        stroke="#2563EB"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function SignUpForm({ onSwitchToLogin }: SignUpFormProps): JSX.Element {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState(false);

  function patch<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    setError(null);
    sydonAuthApi
      .register({
        email: form.email,
        password: form.password,
        confirm_password: form.confirmPassword,
        organization_name: form.organizationName,
        first_name: form.firstName,
        last_name: form.lastName,
      })
      .then(() => setCreated(true))
      .catch((e: Error) => setError(e.message))
      .finally(() => setSubmitting(false));
  }

  if (created) {
    return (
      <AuthShell headline="You're all set" subtext="Your Sydon account has been created.">
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <CheckCircleIcon />
          </div>
          <div style={{ fontSize: 14, color: '#334155', fontWeight: 500, marginBottom: 20 }}>
            Account created. Log in to continue.
          </div>
          <button type="button" onClick={onSwitchToLogin} style={PRIMARY_BUTTON_STYLE}>
            Continue to Log In
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      headline="Create your Sydon account"
      subtext="One account gets you into STAC, the agent dashboard, and Agent Gate."
      footer={<SwitchToLoginLink onClick={onSwitchToLogin} />}
    >
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={LABEL_STYLE}>First Name</div>
            <input
              required
              value={form.firstName}
              onChange={(e) => patch('firstName', e.target.value)}
              style={INPUT_STYLE}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={LABEL_STYLE}>Last Name</div>
            <input
              required
              value={form.lastName}
              onChange={(e) => patch('lastName', e.target.value)}
              style={INPUT_STYLE}
            />
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={LABEL_STYLE}>Organization Name</div>
          <input
            required
            value={form.organizationName}
            onChange={(e) => patch('organizationName', e.target.value)}
            placeholder="Acme Commerce LLC"
            style={INPUT_STYLE}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={LABEL_STYLE}>Email</div>
          <input
            required
            type="email"
            value={form.email}
            onChange={(e) => patch('email', e.target.value)}
            style={INPUT_STYLE}
          />
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
          <div style={{ flex: 1 }}>
            <div style={LABEL_STYLE}>Password</div>
            <input
              required
              type="password"
              value={form.password}
              onChange={(e) => patch('password', e.target.value)}
              style={INPUT_STYLE}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={LABEL_STYLE}>Confirm Password</div>
            <input
              required
              type="password"
              value={form.confirmPassword}
              onChange={(e) => patch('confirmPassword', e.target.value)}
              style={INPUT_STYLE}
            />
          </div>
        </div>

        {error && <div style={ERROR_BANNER_STYLE}>{error}</div>}

        <button
          type="submit"
          disabled={submitting}
          style={{
            ...PRIMARY_BUTTON_STYLE,
            background: submitting ? '#94A3B8' : '#2563EB',
            cursor: submitting ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? 'Creating account…' : 'Create Account'}
        </button>
      </form>
    </AuthShell>
  );
}
